import { useEffect, useRef, useState } from "react";
import {
	MdDragIndicator,
	MdFormatAlignCenter,
	MdFormatAlignLeft,
	MdFormatAlignRight,
	MdVerticalAlignBottom,
	MdVerticalAlignCenter,
	MdVerticalAlignTop,
} from "react-icons/md";
import { cloneDeep, isEqual } from "lodash";
import ReportImageModal from "../ReportImageModal";
import LoadingCircle from "../../utility/LoadingCircle";
import {
	coverElementImageSrc,
	createImageElement,
	createTextElement,
	displayText,
	cssObjectPosition,
	elementAlign,
	elementLabel,
	elementValign,
	isLockedCoverElement,
	ensureCoverInstructions,
	pageMetrics,
} from "./coverInstructions";

const preloadImage = (src) =>
	new Promise((resolve) => {
		if (!src) {
			resolve();
			return;
		}
		const image = new Image();
		image.onload = () => resolve();
		image.onerror = () => resolve();
		image.src = src;
	});

const GRID = 8;
const FONTS = ["Times-Roman", "Times-Bold", "Helvetica"];
const HORIZONTAL_ALIGNS = [
	{ value: "left", label: "Align left", Icon: MdFormatAlignLeft },
	{ value: "center", label: "Align center", Icon: MdFormatAlignCenter },
	{ value: "right", label: "Align right", Icon: MdFormatAlignRight },
];
const VERTICAL_ALIGNS = [
	{ value: "top", label: "Align top", Icon: MdVerticalAlignTop },
	{ value: "middle", label: "Align middle", Icon: MdVerticalAlignCenter },
	{ value: "bottom", label: "Align bottom", Icon: MdVerticalAlignBottom },
];

const AlignGroup = ({ label, value, options, onChange }) => (
	<div className="flex flex-col gap-1">
		<span className="text-stone-600">{label}</span>
		<div className="flex w-fit overflow-hidden rounded-md border border-stone-300">
			{options.map((option) => {
				const selected = value === option.value;
				const Icon = option.Icon;
				return (
					<button
						key={option.value}
						type="button"
						title={option.label}
						aria-label={option.label}
						aria-pressed={selected}
						onClick={() => onChange(option.value)}
						className={`px-2 py-1.5 ${selected ? "bg-blue-100 text-blue-700" : "bg-white text-stone-600 hover:bg-stone-50"}`}>
						<Icon size={18} />
					</button>
				);
			})}
		</div>
	</div>
);

const snap = (value) => Math.round(value / GRID) * GRID;

const RESIZE_HANDLES = [
	{ edge: "n", className: "left-1/2 top-0 -translate-x-1/2 -translate-y-1/2 w-3 h-2 cursor-ns-resize" },
	{ edge: "s", className: "left-1/2 bottom-0 -translate-x-1/2 translate-y-1/2 w-3 h-2 cursor-ns-resize" },
	{ edge: "e", className: "right-0 top-1/2 translate-x-1/2 -translate-y-1/2 w-2 h-3 cursor-ew-resize" },
	{ edge: "w", className: "left-0 top-1/2 -translate-x-1/2 -translate-y-1/2 w-2 h-3 cursor-ew-resize" },
	{ edge: "nw", className: "left-0 top-0 -translate-x-1/2 -translate-y-1/2 w-2.5 h-2.5 cursor-nwse-resize" },
	{ edge: "ne", className: "right-0 top-0 translate-x-1/2 -translate-y-1/2 w-2.5 h-2.5 cursor-nesw-resize" },
	{ edge: "sw", className: "left-0 bottom-0 -translate-x-1/2 translate-y-1/2 w-2.5 h-2.5 cursor-nesw-resize" },
	{ edge: "se", className: "right-0 bottom-0 translate-x-1/2 translate-y-1/2 w-2.5 h-2.5 cursor-nwse-resize" },
];

const boxHeight = (element) => element.height || Math.max(GRID * 2, (element.size || 12) * 1.4);

const textFontStyle = (element) => ({
	fontFamily: String(element.font || "").startsWith("Helvetica") ? "Helvetica, Arial, sans-serif" : 'Times, "Times New Roman", serif',
	fontWeight: String(element.font || "").includes("Bold") ? "700" : "400",
	fontSize: `${Number(element.size) || 12}px`,
	lineHeight: "1.2",
});

/** Width and height, in points, that hold this text with no extra space. */
const measureTextBox = (text, element, maxWidth) => {
	const probe = document.createElement("div");
	const font = textFontStyle(element);
	Object.assign(probe.style, font, {
		position: "absolute",
		visibility: "hidden",
		left: "-9999px",
		top: "0",
		whiteSpace: "nowrap",
		width: "max-content",
	});
	probe.textContent = text;
	document.body.appendChild(probe);
	const naturalWidth = probe.offsetWidth;
	let width = naturalWidth;
	let height = probe.offsetHeight;
	if (naturalWidth > maxWidth) {
		probe.style.whiteSpace = "normal";
		probe.style.width = `${maxWidth}px`;
		width = maxWidth;
		height = probe.offsetHeight;
	}
	document.body.removeChild(probe);
	return { width: Math.max(GRID, width), height: Math.max(GRID, height) };
};

const textForFit = (element, reportName) => {
	if (element.binding === "signature") return "Signature";
	if (element.binding === "createdDate") return "Report Created: 00/00/0000";
	return displayText(element, { reportName, dateString: "Report Created: 00/00/0000" });
};

const MetricField = ({ label, value, onCommit }) => {
	const [draft, setDraft] = useState(() => String(Math.round(value)));
	const scrub = useRef(null);

	useEffect(() => {
		setDraft(String(Math.round(value)));
	}, [value]);

	const commit = () => {
		const parsed = Number(String(draft).trim());
		if (!Number.isFinite(parsed)) {
			setDraft(String(Math.round(value)));
			return;
		}
		onCommit(parsed);
	};

	const onScrubDown = (e) => {
		if (e.button !== 0) return;
		e.preventDefault();
		e.currentTarget.setPointerCapture(e.pointerId);
		scrub.current = { startX: e.clientX, startValue: Number(value) || 0 };
	};

	const onScrubMove = (e) => {
		if (!scrub.current || !e.currentTarget.hasPointerCapture?.(e.pointerId)) return;
		const delta = e.clientX - scrub.current.startX;
		onCommit(Math.round(scrub.current.startValue + delta));
	};

	const onScrubUp = (e) => {
		if (e.currentTarget.hasPointerCapture?.(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);
		scrub.current = null;
	};

	return (
		<div className="flex flex-col gap-1">
			<span
				className="text-stone-600 cursor-ew-resize select-none w-fit"
				title="Drag left or right to change"
				onPointerDown={onScrubDown}
				onPointerMove={onScrubMove}
				onPointerUp={onScrubUp}
				onPointerCancel={onScrubUp}>
				{label}
			</span>
			<input
				type="text"
				inputMode="decimal"
				value={draft}
				onChange={(e) => setDraft(e.target.value)}
				onBlur={commit}
				onKeyDown={(e) => {
					if (e.key === "Enter") e.currentTarget.blur();
				}}
				className="border border-stone-300 rounded-md px-2 py-1.5"
			/>
		</div>
	);
};

const CoverEditModal = ({ api, token, reportId, reportName, coverImgName, coverInstructions, onClose, onApply }) => {
	const [instructions, setInstructions] = useState(() =>
		cloneDeep(ensureCoverInstructions({ reportName, coverImgName, coverInstructions }).coverInstructions),
	);
	const initialInstructions = useRef(null);
	if (!initialInstructions.current) initialInstructions.current = cloneDeep(instructions);
	const coverChanged = !isEqual(instructions, initialInstructions.current);
	const [selectedId, setSelectedId] = useState(null);
	const [showGrid, setShowGrid] = useState(true);
	const [showImageBackground, setShowImageBackground] = useState(true);
	const [showPositionHelp, setShowPositionHelp] = useState(false);
	const [pagePx, setPagePx] = useState({ w: 0, h: 0 });
	const [dragging, setDragging] = useState(null);
	const [resizing, setResizing] = useState(null);
	const [logos, setLogos] = useState([]);
	const [brandLogo, setBrandLogo] = useState("");
	const [assetsReady, setAssetsReady] = useState(false);
	const [forceReady, setForceReady] = useState(false);
	const [showImagePicker, setShowImagePicker] = useState(false);
	const [dragLayerId, setDragLayerId] = useState(null);
	const [dropIndex, setDropIndex] = useState(null);
	const [saving, setSaving] = useState(false);
	const canvasHostRef = useRef(null);

	const page = pageMetrics(instructions.page);
	const selected = instructions.elements.find((element) => element.id === selectedId) || null;
	const thumbnail = logos.find((logo) => logo.name === selected?.name)?.thumbnail || "";

	useEffect(() => {
		const el = canvasHostRef.current;
		if (!el) return;
		const measure = () => {
			const { width, height } = el.getBoundingClientRect();
			if (width < 2 || height < 2) return;
			const scale = Math.min((width - 32) / page.width, (height - 32) / page.height);
			setPagePx({ w: page.width * scale, h: page.height * scale });
		};
		measure();
		const observer = new ResizeObserver(measure);
		observer.observe(el);
		return () => observer.disconnect();
	}, [page.width, page.height]);

	useEffect(() => {
		const timer = setTimeout(() => setForceReady(true), 4000);
		return () => clearTimeout(timer);
	}, []);

	useEffect(() => {
		if (!api || !token) {
			setAssetsReady(true);
			return;
		}
		let cancelled = false;
		fetch(`${api}/report/logos`, {
			method: "GET",
			headers: { Authorization: "Bearer " + token },
		})
			.then((res) => (res.ok ? res.json() : null))
			.then(async (data) => {
				if (cancelled || !data) return;
				const nextLogos = data.logos || [];
				const nextBrand = data.brandLogo || "";
				setLogos(nextLogos);
				setBrandLogo(nextBrand);
				const srcs = [
					...new Set(
						instructions.elements
							.filter((element) => element.type === "image")
							.map((element) => coverElementImageSrc(element, nextLogos, nextBrand))
							.filter(Boolean),
					),
				];
				await Promise.all(srcs.map(preloadImage));
			})
			.catch(() => {})
			.finally(() => {
				if (!cancelled) setAssetsReady(true);
			});
		return () => {
			cancelled = true;
		};
		// Preload the images on this cover once, when the editor opens.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [api, token]);

	const editorReady = forceReady || (assetsReady && pagePx.w > 0);

	const updateElement = (id, patch) => {
		setInstructions((prev) => ({
			...prev,
			elements: prev.elements.map((element) => (element.id === id ? { ...element, ...patch } : element)),
		}));
	};

	const placeFittedBox = (element, width, height) => {
		const oldW = element.width || GRID * 4;
		const oldH = boxHeight(element);
		let x = element.x;
		let y = element.y;
		const align = elementAlign(element);
		const valign = elementValign(element);
		if (align === "center") x += (oldW - width) / 2;
		else if (align === "right") x += oldW - width;
		if (valign === "middle") y += (oldH - height) / 2;
		else if (valign === "bottom") y += oldH - height;
		width = Math.min(page.width, Math.max(GRID, width));
		height = Math.min(page.height, Math.max(GRID, height));
		x = Math.max(0, Math.min(page.width - width, x));
		y = Math.max(0, Math.min(page.height - height, y));
		return { x, y, width, height };
	};

	const stretchBox = (axis) => {
		if (!selected) return;
		if (axis === "width") updateElement(selected.id, { x: 0, width: page.width });
		else updateElement(selected.id, { y: 0, height: page.height });
	};

	const fitContent = () => {
		if (!selected) return;
		if (selected.type === "image") {
			const src = coverElementImageSrc(selected, logos, brandLogo);
			if (!src) return;
			const image = new Image();
			image.onload = () => {
				const natW = image.naturalWidth;
				const natH = image.naturalHeight;
				if (!natW || !natH) return;
				const boxW = selected.width || GRID * 4;
				const boxH = boxHeight(selected);
				const scale = Math.min(boxW / natW, boxH / natH);
				updateElement(selected.id, placeFittedBox(selected, natW * scale, natH * scale));
			};
			image.src = src;
			return;
		}
		const text = textForFit(selected, reportName);
		if (!String(text).trim()) return;
		const measured = measureTextBox(text, selected, page.width);
		updateElement(selected.id, placeFittedBox(selected, measured.width, measured.height));
	};

	useEffect(() => {
		if (!dragging) return;
		const onMove = (e) => {
			const x = snap((e.clientX - dragging.pageLeft) / dragging.scale - dragging.offsetX);
			const y = snap((e.clientY - dragging.pageTop) / dragging.scale - dragging.offsetY);
			setInstructions((prev) => ({
				...prev,
				elements: prev.elements.map((element) => {
					if (element.id !== dragging.id) return element;
					const height = boxHeight(element);
					return {
						...element,
						height,
						x: Math.max(0, Math.min(page.width - (element.width || GRID), x)),
						y: Math.max(0, Math.min(page.height - height, y)),
					};
				}),
			}));
		};
		const onUp = () => setDragging(null);
		window.addEventListener("pointermove", onMove);
		window.addEventListener("pointerup", onUp);
		return () => {
			window.removeEventListener("pointermove", onMove);
			window.removeEventListener("pointerup", onUp);
		};
	}, [dragging, page.width, page.height]);

	useEffect(() => {
		if (!resizing) return;
		const onMove = (e) => {
			const dx = (e.clientX - resizing.startX) / resizing.scale;
			const dy = (e.clientY - resizing.startY) / resizing.scale;
			const edge = resizing.edge;
			const min = GRID * 2;
			let x = resizing.origX;
			let y = resizing.origY;
			let width = resizing.origW;
			let height = resizing.origH;

			if (edge.includes("e")) width = snap(resizing.origW + dx);
			if (edge.includes("s")) height = snap(resizing.origH + dy);
			if (edge.includes("w")) {
				width = snap(resizing.origW - dx);
				x = resizing.origX + resizing.origW - width;
			}
			if (edge.includes("n")) {
				height = snap(resizing.origH - dy);
				y = resizing.origY + resizing.origH - height;
			}

			if (width < min) {
				if (edge.includes("w")) x = resizing.origX + resizing.origW - min;
				width = min;
			}
			if (height < min) {
				if (edge.includes("n")) y = resizing.origY + resizing.origH - min;
				height = min;
			}
			if (x < 0) {
				width += x;
				x = 0;
			}
			if (y < 0) {
				height += y;
				y = 0;
			}
			if (x + width > page.width) width = page.width - x;
			if (y + height > page.height) height = page.height - y;

			updateElement(resizing.id, {
				x,
				y,
				width: Math.max(min, width),
				height: Math.max(min, height),
			});
		};
		const onUp = () => setResizing(null);
		window.addEventListener("pointermove", onMove);
		window.addEventListener("pointerup", onUp);
		return () => {
			window.removeEventListener("pointermove", onMove);
			window.removeEventListener("pointerup", onUp);
		};
	}, [resizing, page.width, page.height]);

	const onPointerDown = (e, element) => {
		e.preventDefault();
		e.stopPropagation();
		setSelectedId(element.id);
		const pageEl = e.currentTarget.closest("[data-cover-page]");
		const rect = pageEl.getBoundingClientRect();
		const scale = rect.width / page.width;
		setDragging({
			id: element.id,
			offsetX: (e.clientX - rect.left) / scale - element.x,
			offsetY: (e.clientY - rect.top) / scale - element.y,
			scale,
			pageLeft: rect.left,
			pageTop: rect.top,
		});
	};

	const onResizeDown = (e, element, edge) => {
		e.preventDefault();
		e.stopPropagation();
		setSelectedId(element.id);
		const pageEl = e.currentTarget.closest("[data-cover-page]");
		const rect = pageEl.getBoundingClientRect();
		setResizing({
			id: element.id,
			edge,
			scale: rect.width / page.width,
			startX: e.clientX,
			startY: e.clientY,
			origX: element.x,
			origY: element.y,
			origW: element.width || GRID * 4,
			origH: boxHeight(element),
		});
	};

	const addElement = (element) => {
		const next = { ...element, x: snap(element.x), y: snap(element.y), width: snap(element.width), height: snap(element.height) };
		setInstructions((prev) => ({ ...prev, elements: [...prev.elements, next] }));
		setSelectedId(next.id);
	};

	const removeSelected = () => {
		if (!selected || isLockedCoverElement(selected)) return;
		setInstructions((prev) => ({ ...prev, elements: prev.elements.filter((element) => element.id !== selected.id) }));
		setSelectedId(null);
	};

	const layerOrder = [...instructions.elements].reverse();

	const reorderLayer = (toIndex) => {
		if (!dragLayerId) return;
		setInstructions((prev) => {
			const display = [...prev.elements].reverse();
			const fromIndex = display.findIndex((element) => element.id === dragLayerId);
			if (fromIndex < 0) return prev;
			let insertAt = toIndex;
			if (fromIndex < insertAt) insertAt -= 1;
			if (fromIndex === insertAt) return prev;
			const next = [...display];
			const [moved] = next.splice(fromIndex, 1);
			next.splice(insertAt, 0, moved);
			return { ...prev, elements: next.reverse() };
		});
		setDragLayerId(null);
		setDropIndex(null);
	};

	const apply = async (e) => {
		e.preventDefault();
		if (saving || !coverChanged) return;
		const title = instructions.elements.find((element) => element.binding === "reportName");
		if (title && !String(title.text || "").trim()) return;
		setSaving(true);
		try {
			await onApply(cloneDeep(instructions));
			onClose();
		} catch (err) {
			alert(err.message || "Could not save cover.");
			setSaving(false);
		}
	};

	const pageScale = pagePx.w > 0 ? pagePx.w / page.width : 1;

	return (
		<div className="absolute inset-0 z-[450] bg-black/60 flex items-center justify-center p-3">
			{!editorReady ? (
				<div
					className="absolute z-30 bg-white rounded-xl shadow-2xl border border-stone-200 px-8 py-6 flex flex-col items-center gap-3"
					onClick={(e) => e.stopPropagation()}>
					<LoadingCircle />
					<p className="text-sm text-stone-700">Loading cover</p>
				</div>
			) : null}
			<form
				onSubmit={apply}
				onClick={(e) => e.stopPropagation()}
				className={`w-full h-full max-w-[1400px] bg-stone-100 text-stone-900 rounded-xl shadow-2xl border border-stone-300 overflow-hidden flex flex-col ${
					editorReady ? "" : "invisible"
				}`}>
				<div className="shrink-0 bg-stone-800 text-stone-50 px-4 py-2.5 flex flex-wrap items-center justify-between gap-2">
					<div>
						<p className="text-base font-semibold">Edit cover</p>
						<p className="text-xs text-stone-300">A4 · drag on the grid · {GRID}pt snap · Apply saves the cover</p>
					</div>
					<div className="flex flex-wrap items-center gap-2">
						<label className="flex items-center gap-2 text-sm bg-stone-700 px-3 py-1 rounded cursor-pointer">
							<input type="checkbox" checked={showGrid} onChange={(e) => setShowGrid(e.target.checked)} />
							Snap grid ({GRID}pt)
						</label>
						<label className="flex items-center gap-2 text-sm bg-stone-700 px-3 py-1 rounded cursor-pointer">
							<input type="checkbox" checked={showImageBackground} onChange={(e) => setShowImageBackground(e.target.checked)} />
							Image backgrounds
						</label>
						<button type="button" onClick={onClose} className="text-sm px-3 py-1 rounded bg-stone-700 hover:bg-stone-600">
							Cancel
						</button>
						<button
							type="submit"
							disabled={saving || !coverChanged}
							className="text-sm px-3 py-1 rounded bg-amber-500 text-stone-900 font-medium hover:bg-amber-400 disabled:opacity-50 disabled:cursor-not-allowed">
							{saving ? "Saving…" : "Apply"}
						</button>
					</div>
				</div>

				<div className="grid lg:grid-cols-[200px_minmax(0,1fr)_280px] flex-1 min-h-0 overflow-hidden">
					<aside className="bg-white border-r border-stone-200 p-3 flex flex-col gap-3 min-h-0 overflow-hidden">
						<div className="shrink-0 flex flex-col gap-3">
							<p className="text-xs uppercase tracking-wide text-stone-500 font-semibold">Add</p>
							<button
								type="button"
								onClick={() => addElement(createTextElement())}
								className="text-left text-sm px-3 py-2 rounded border border-stone-200 hover:bg-amber-50">
								Text
								<span className="block text-[11px] text-stone-400">Drop a text block on the page</span>
							</button>
							<button
								type="button"
								onClick={() => addElement(createImageElement())}
								className="text-left text-sm px-3 py-2 rounded border border-stone-200 hover:bg-amber-50">
								Image
								<span className="block text-[11px] text-stone-400">Drop an image block on the page</span>
							</button>
						</div>
						<div className="border-t border-stone-100 pt-3 flex flex-col gap-1 min-h-0 flex-1">
							<p className="shrink-0 text-xs uppercase tracking-wide text-stone-500 font-semibold">Layers</p>
							<p className="shrink-0 text-[11px] text-stone-400">Top of the list is in front. Drag to restack.</p>
							<div className="flex-1 min-h-0 overflow-y-auto flex flex-col gap-1">
								{layerOrder.map((element, index) => (
									<div
										key={element.id}
										draggable
										onDragStart={() => setDragLayerId(element.id)}
										onDragOver={(e) => {
											e.preventDefault();
											setDropIndex(index);
										}}
										onDrop={(e) => {
											e.preventDefault();
											reorderLayer(index);
										}}
										onDragEnd={() => {
											setDragLayerId(null);
											setDropIndex(null);
										}}
										onClick={() => setSelectedId(element.id)}
										title={elementLabel(element)}
										className={`shrink-0 flex items-center gap-1 text-left text-sm px-1 py-1.5 rounded truncate cursor-grab active:cursor-grabbing ${
											element.id === selectedId ? "bg-blue-100" : "hover:bg-stone-50"
										} ${dropIndex === index ? "border-t-2 border-blue-600" : "border-t-2 border-transparent"} ${
											dragLayerId === element.id ? "opacity-50" : ""
										}`}>
										<MdDragIndicator className="shrink-0 text-stone-400" />
										<span className="truncate">{elementLabel(element)}</span>
									</div>
								))}
								<div
									className={`shrink-0 h-3 ${dropIndex === layerOrder.length ? "border-t-2 border-blue-600" : ""}`}
									onDragOver={(e) => {
										e.preventDefault();
										setDropIndex(layerOrder.length);
									}}
									onDrop={(e) => {
										e.preventDefault();
										reorderLayer(layerOrder.length);
									}}
								/>
							</div>
						</div>
					</aside>

					<section ref={canvasHostRef} className="min-h-0 overflow-auto flex items-center justify-center p-4">
						{pagePx.w > 0 ? (
							<div
								data-cover-page
								className="relative bg-white shadow-lg border border-stone-300 shrink-0"
								style={{ width: pagePx.w, height: pagePx.h }}
								onClick={() => setSelectedId(null)}>
								{showGrid ? (
									<div
										className="absolute inset-0 pointer-events-none opacity-40"
										style={{
											backgroundImage: `
												linear-gradient(to right, #d6d3d1 1px, transparent 1px),
												linear-gradient(to bottom, #d6d3d1 1px, transparent 1px)
											`,
											backgroundSize: `${(GRID / page.width) * 100}% ${(GRID / page.height) * 100}%`,
										}}
									/>
								) : null}
								{instructions.elements.map((element) => {
									const height = boxHeight(element);
									const isText = element.type === "text";
									const imageSrc = isText ? "" : coverElementImageSrc(element, logos, brandLogo);
									return (
										<div
											key={element.id}
											role="button"
											tabIndex={0}
											onPointerDown={(e) => onPointerDown(e, element)}
											onClick={(e) => {
												e.stopPropagation();
												setSelectedId(element.id);
											}}
											className={`absolute cursor-grab active:cursor-grabbing box-border overflow-hidden ${
												selectedId === element.id
													? "outline outline-2 outline-blue-600"
													: "hover:outline hover:outline-1 hover:outline-stone-400"
											} ${!isText && showImageBackground ? "bg-stone-100 border border-stone-300" : ""}`}
											style={{
												left: `${(element.x / page.width) * 100}%`,
												top: `${(element.y / page.height) * 100}%`,
												width: `${((element.width || GRID * 4) / page.width) * 100}%`,
												height: `${(height / page.height) * 100}%`,
												fontFamily: String(element.font || "").startsWith("Helvetica")
													? "Helvetica, Arial, sans-serif"
													: 'Times, "Times New Roman", serif',
												fontWeight: String(element.font || "").includes("Bold") ? 700 : 400,
												fontSize: `${Math.max(8, (element.size || 12) * pageScale)}px`,
												textAlign: elementAlign(element),
												lineHeight: 1.2,
												display: "flex",
												alignItems:
													elementValign(element) === "top"
														? "flex-start"
														: elementValign(element) === "bottom"
															? "flex-end"
															: "center",
											}}>
											{isText ? (
												<span className="w-full">{displayText(element, { reportName, dateString: "Report Created: —" })}</span>
											) : imageSrc ? (
												<img
													src={imageSrc}
													alt=""
													draggable={false}
													className="w-full h-full object-contain pointer-events-none"
													style={{ objectPosition: cssObjectPosition(element) }}
												/>
											) : (
												<span className="text-[10px] text-stone-500 flex h-full items-center justify-center text-center pointer-events-none">
													{element.binding === "brandLogo" ? "1st Light logo" : element.name || "Image"}
												</span>
											)}
											{selectedId === element.id
												? RESIZE_HANDLES.map((handle) => (
														<span
															key={handle.edge}
															role="presentation"
															onPointerDown={(e) => onResizeDown(e, element, handle.edge)}
															className={`absolute bg-blue-600 border border-white z-10 ${handle.className}`}
														/>
													))
												: null}
										</div>
									);
								})}
							</div>
						) : null}
					</section>

					<aside className="bg-white border-l border-stone-200 p-3 overflow-y-auto min-h-0">
						<p className="text-xs uppercase tracking-wide text-stone-500 font-semibold mb-3">Selected</p>
						{!selected ? (
							<p className="text-sm text-stone-500">Click a block on the page.</p>
						) : (
							<div className="flex flex-col gap-3 text-sm">
								<div className="flex items-end justify-between gap-2">
									<label className="flex flex-col gap-1 flex-1 min-w-0">
										<span className="text-stone-600">Name</span>
										<input
											type="text"
											value={selected.label || ""}
											placeholder={elementLabel({ ...selected, label: "" })}
											onChange={(e) => updateElement(selected.id, { label: e.target.value })}
											className="border border-stone-300 rounded-md px-2 py-1.5"
										/>
									</label>
									{isLockedCoverElement(selected) ? null : (
										<button
											type="button"
											onClick={removeSelected}
											className="text-xs px-2 py-1.5 rounded border border-red-200 text-red-700">
											Remove
										</button>
									)}
								</div>
								<div className="flex flex-col gap-1.5">
									<div className="flex items-center justify-between">
										<span className="text-stone-600">Position</span>
										<button
											type="button"
											aria-expanded={showPositionHelp}
											aria-label="How to change position"
											onClick={() => setShowPositionHelp((open) => !open)}
											className="w-5 h-5 rounded-full border border-stone-300 text-[11px] font-semibold leading-none text-stone-500 hover:bg-stone-50">
											i
										</button>
									</div>
									{showPositionHelp ? (
										<p className="text-xs text-stone-500 leading-snug">
											Drag the X, Y, W, or H label left or right to change the number. You can also type a value and press Enter.
										</p>
									) : null}
									<div className="grid grid-cols-2 gap-2">
										<MetricField label="X" value={selected.x} onCommit={(x) => updateElement(selected.id, { x })} />
										<MetricField label="Y" value={selected.y} onCommit={(y) => updateElement(selected.id, { y })} />
										<MetricField label="W" value={selected.width || 0} onCommit={(width) => updateElement(selected.id, { width })} />
										<MetricField label="H" value={boxHeight(selected)} onCommit={(height) => updateElement(selected.id, { height })} />
									</div>
								</div>
								<div className="flex flex-col gap-1">
									<span className="text-stone-600">Box</span>
									<div className="flex flex-wrap gap-1">
										<button
											type="button"
											title="Make the box as wide as the page"
											onClick={() => stretchBox("width")}
											className="text-xs px-2 py-1.5 rounded border border-stone-300 text-stone-700 hover:bg-stone-50">
											Stretch width
										</button>
										<button
											type="button"
											title="Make the box as tall as the page"
											onClick={() => stretchBox("height")}
											className="text-xs px-2 py-1.5 rounded border border-stone-300 text-stone-700 hover:bg-stone-50">
											Stretch height
										</button>
										<button
											type="button"
											title="Size the box to the text or image"
											onClick={fitContent}
											className="text-xs px-2 py-1.5 rounded border border-stone-300 text-stone-700 hover:bg-stone-50">
											Fit content
										</button>
									</div>
								</div>
								<AlignGroup
									label="Horizontal"
									value={elementAlign(selected)}
									options={HORIZONTAL_ALIGNS}
									onChange={(align) => updateElement(selected.id, { align })}
								/>
								<AlignGroup
									label="Vertical"
									value={elementValign(selected)}
									options={VERTICAL_ALIGNS}
									onChange={(valign) => updateElement(selected.id, { valign })}
								/>
								{selected.type === "text" && selected.binding !== "createdDate" && selected.binding !== "signature" ? (
									<label className="flex flex-col gap-1">
										<span className="text-stone-600">Text</span>
										<input
											type="text"
											value={selected.text || ""}
											onChange={(e) => updateElement(selected.id, { text: e.target.value })}
											className="border border-stone-300 rounded-md px-2 py-1.5"
										/>
									</label>
								) : null}
								{selected.binding === "createdDate" ? (
									<p className="text-stone-500">Filled with the created date when the cover is drawn.</p>
								) : null}
								{selected.binding === "signature" ? (
									<p className="text-stone-500">Signature label. The generated cover keeps a fillable field in this spot.</p>
								) : null}
								{selected.type === "image" && selected.binding === "brandLogo" ? (
									<p className="text-stone-500">Company logo, placed when the cover PDF is generated.</p>
								) : null}
								{selected.type === "image" && selected.binding !== "brandLogo" ? (
									<div className="flex flex-col gap-2">
										<div className="h-20 rounded border border-stone-200 bg-stone-50 flex items-center justify-center overflow-hidden">
											{thumbnail ? (
												<img src={thumbnail} alt="" className="max-h-full max-w-full object-contain" />
											) : (
												<p className="text-stone-400">{selected.name || "No image"}</p>
											)}
										</div>
										<button
											type="button"
											onClick={() => setShowImagePicker(true)}
											className="self-start px-3 py-1.5 rounded-md bg-stone-800 text-white hover:bg-stone-700">
											{selected.name ? "Change image" : "Choose image"}
										</button>
									</div>
								) : null}
								{selected.type === "text" ? (
									<>
										<label className="flex flex-col gap-1">
											<span className="text-stone-600">Font</span>
											<select
												value={selected.font}
												onChange={(e) => updateElement(selected.id, { font: e.target.value })}
												className="border border-stone-300 rounded-md px-2 py-1.5 bg-white">
												{FONTS.map((font) => (
													<option key={font} value={font}>
														{font}
													</option>
												))}
											</select>
										</label>
										<label className="flex flex-col gap-1">
											<span className="text-stone-600">Size</span>
											<input
												type="number"
												value={selected.size}
												onChange={(e) => updateElement(selected.id, { size: Number(e.target.value) || selected.size })}
												className="border border-stone-300 rounded-md px-2 py-1.5"
											/>
										</label>
									</>
								) : null}
							</div>
						)}
					</aside>
				</div>
			</form>

			{showImagePicker && selected?.type === "image" ? (
				<ReportImageModal
					logos={logos}
					setShowReportImageModal={setShowImagePicker}
					selectedImgName={selected.name || ""}
					setSelectedImgName={(name) => updateElement(selected.id, { name })}
					reportId={reportId}
					api={api}
					token={token}
					setLogos={setLogos}
				/>
			) : null}
		</div>
	);
};

export default CoverEditModal;
